import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, CloseButton } from "@heroui/react";
import markUrl from "@/assets/brand/vp-app-icon.svg";
import { useDeskMode } from "@/lib/mode";

/**
 * First-visit product tour (Quicken / Calendly / beehiiv pattern on Mobbin):
 * a dismissible welcome card, then numbered steps that spotlight one part of
 * the Simple desk at a time. Re-open any time with the "?" in the top bar
 * (it dispatches TOUR_EVENT).
 */
export const TOUR_EVENT = "vp:tour";
const DONE_KEY = "vp.tour.done";

const STEPS = [
	{ target: "answer", title: "Your answer for right now", body: "This box tells you in one line whether there's a trade. Most of the time it says no, and that's normal." },
	{ target: "coins", title: "Your coins", body: "One card per coin. Tap a card to see what it's waiting for, in plain words, or the step-by-step plan when it's ready." },
	{ target: "safety", title: "Your safety limit", body: "How much you can still lose today. When it's used up, the desk tells you to stop. This protects you on bad days." },
	{ target: "mode", title: "Simple or Pro", body: "Want charts, levels and the full checklist? Switch to Pro here. You can switch back any time." },
] as const;

function isDone(): boolean {
	try { return localStorage.getItem(DONE_KEY) === "1"; } catch { return false; }
}
function markDone() {
	try { localStorage.setItem(DONE_KEY, "1"); } catch { /* shows again next visit */ }
}

type Phase = { kind: "idle" } | { kind: "welcome" } | { kind: "step"; i: number };

export function WelcomeTour() {
	const [phase, setPhase] = useState<Phase>({ kind: "idle" });
	const { pathname } = useLocation();
	const navigate = useNavigate();
	const [, setMode] = useDeskMode();

	// First visit to the desk: offer the tour (never interrupt other pages).
	useEffect(() => {
		if (pathname === "/" && !isDone()) setPhase(p => (p.kind === "idle" ? { kind: "welcome" } : p));
	}, [pathname]);

	// "?" in the top bar: start straight at step 1 on the Simple desk.
	useEffect(() => {
		const start = () => {
			setMode("simple");
			navigate("/");
			setPhase({ kind: "step", i: 0 });
		};
		window.addEventListener(TOUR_EVENT, start);
		return () => window.removeEventListener(TOUR_EVENT, start);
	}, [navigate, setMode]);

	const finish = useCallback(() => { markDone(); setPhase({ kind: "idle" }); }, []);

	useEffect(() => {
		if (phase.kind === "idle") return;
		const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") finish(); };
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [phase.kind, finish]);

	if (phase.kind === "idle") return null;

	if (phase.kind === "welcome") {
		return (
			<div role="dialog" aria-labelledby="tour-welcome-title" className="toast-dock overflow-hidden md:w-[340px] rounded-card border border-border bg-overlay shadow-overlay">
				<div className="flex items-center justify-center bg-accent-tint py-6">
					<img src={markUrl} alt="" className="size-14 rounded-2xl" />
				</div>
				<div className="p-4">
					<h2 id="tour-welcome-title" className="text-base font-semibold">Welcome to VP Signal</h2>
					<p className="mt-1 text-sm text-muted">Take a 1-minute tour to see how the desk tells you when, and when not, to trade.</p>
					<div className="mt-4 flex justify-end gap-2">
						<Button size="sm" variant="ghost" onPress={finish}>No thanks</Button>
						<Button size="sm" variant="primary" onPress={() => { setMode("simple"); setPhase({ kind: "step", i: 0 }); }}>Show me around</Button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<TourStep
			index={phase.i}
			onBack={() => setPhase({ kind: "step", i: Math.max(0, phase.i - 1) })}
			onNext={() => (phase.i + 1 < STEPS.length ? setPhase({ kind: "step", i: phase.i + 1 }) : finish())}
			onClose={finish}
		/>
	);
}

function TourStep({ index, onBack, onNext, onClose }: { index: number; onBack: () => void; onNext: () => void; onClose: () => void }) {
	const step = STEPS[index]!;
	const [rect, setRect] = useState<DOMRect | null>(null);
	const cardRef = useRef<HTMLDivElement>(null);

	// Track the target's position (it may still be rendering, or the user scrolls).
	useLayoutEffect(() => {
		let raf = 0;
		let scrolled = false;
		const tick = () => {
			// The first one on screen: a target can exist twice (the rail's Pro switch and the phone top bar's), one of them hidden.
			const el = Array.from(document.querySelectorAll(`[data-tour="${step.target}"]`)).find(e => e.getClientRects().length > 0);
			if (el) {
				const r = el.getBoundingClientRect();
				// Only scroll when the target is off-screen: scrolling to something
				// already visible (e.g. inside the fixed nav rail) shifts the page.
				if (!scrolled) {
					scrolled = true;
					if (r.top < 0 || r.bottom > window.innerHeight) el.scrollIntoView({ block: "center", behavior: "smooth" });
				}
				setRect(prev => (prev && prev.top === r.top && prev.left === r.left && prev.width === r.width && prev.height === r.height ? prev : r));
			}
			raf = requestAnimationFrame(tick);
		};
		tick();
		return () => cancelAnimationFrame(raf);
	}, [step.target]);

	useEffect(() => { cardRef.current?.focus(); }, [index]);

	const pad = 8;
	const cardW = Math.min(320, window.innerWidth - 32);
	let top = 16, left = 16;
	if (rect) {
		const below = rect.bottom + pad + 12;
		const fitsBelow = below + 190 < window.innerHeight;
		top = fitsBelow ? below : Math.max(16, rect.top - pad - 12 - 190);
		left = Math.min(Math.max(16, rect.left), window.innerWidth - cardW - 16);
	}

	return (
		<>
			{rect && (
				<div
					aria-hidden
					className="pointer-events-none fixed z-50 rounded-card ring-2 ring-accent transition-all duration-200"
					style={{
						top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2,
						boxShadow: "0 0 0 9999px rgb(0 0 0 / 0.55)",
					}}
				/>
			)}
			<div
				ref={cardRef}
				tabIndex={-1}
				role="dialog"
				aria-labelledby="tour-step-title"
				className="fixed z-50 rounded-card border border-border bg-overlay p-4 shadow-overlay outline-none transition-all duration-200"
				style={{ top, left, width: cardW }}
			>
				<div className="flex items-start justify-between gap-2">
					<h2 id="tour-step-title" className="text-sm font-semibold">{step.title}</h2>
					<CloseButton aria-label="End tour" onPress={onClose} />
				</div>
				<p className="mt-1 text-sm text-muted">{step.body}</p>
				<div className="mt-4 flex items-center justify-between">
					<span className="num text-xs text-muted">{index + 1} of {STEPS.length}</span>
					<div className="flex gap-2">
						{index > 0 && <Button size="sm" variant="ghost" onPress={onBack}>Back</Button>}
						<Button size="sm" variant="primary" onPress={onNext}>{index + 1 < STEPS.length ? "Next" : "Done"}</Button>
					</div>
				</div>
			</div>
		</>
	);
}
